import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import axios from 'axios';
import { StatusManager } from 'common/components/StatusManager';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { ENDPOINTS } from 'features/command-center/constants';
import useDatatable, {
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { rowClickBuilder } from 'hooks/profileActions';
import { useLatestRequestSignal } from 'hooks/useLatestRequestSignal';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import {
  ScrollableDataTable,
  ScrollableDataTableLayout
} from 'UI/components/templates/ScrollableDataTableLayout';
import { RowActions } from 'UI/constants/defaults';
import { UIStatus } from 'UI/constants/status';
import { getErrorMessage, IMAGE_VALIDATION_TEXT } from 'UI/utils';

import { SupportActivityDetails } from '../SupportActivityDetails';

import {
  ACTIVITY_MODE,
  ACTIVITY_TYPE_MAP,
  COLUMNS,
  ORDER_BY_OPTIONS,
  PAGE_KEY
} from './SupportActivityList.constants';
import { formatResponse } from './SupportActivityList.utils';

export const SupportActivityList = ({ employeeNumber }) => {
  const dispatch = useDispatch();

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [uiState, setUIState] = useState({
    noteId: null,
    mode: null,
    isLoading: false,
    isSaving: false
  });
  const [activityStatus, setActivityStatus] = useState(UIStatus.Default);
  const { nextSignal, abort } = useLatestRequestSignal();

  const form = useForm();
  const { getValues, triggerValidation, errors } = form;

  const isUploadingEditorTextImage = errors?.notes?.message === IMAGE_VALIDATION_TEXT;

  const initialPreferences = getColumnPreferences(PAGE_KEY, 0, ORDER_BY_OPTIONS, COLUMNS);
  const {
    columnPreferences,
    count,
    data,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState,
    getData
  } = useDatatable({
    apiInstance: SECONDARY_API_CLIENT,
    columnsDefinitions: COLUMNS,
    endpoint: ENDPOINTS.supportActivitiesByEmployee.replace(':employeeNumber', employeeNumber),
    formatResponse,
    initialPreferences,
    key: PAGE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: null,
    sendDateWithTimezone: true,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const actions = [
    {
      name: RowActions.View,
      onClick: rowClickBuilder({
        columns,
        data,
        pickId: true,
        callback: id => onActivitySelect({ id, mode: ACTIVITY_MODE.View })
      })
    }
  ];

  const actionsColumn = buildRowActionsRendererDefinition({
    actions,
    columns
  });

  const finalColumns = getColumnsToRender([...columns, actionsColumn]);

  const handleCloseDrawer = () => {
    abort();
    setUIState({ isLoading: false, isSaving: false, mode: null, noteId: null });
    setSelectedActivity(null);
    setActivityStatus(UIStatus.Default);
  };

  const onActivitySelect = async ({ id, mode }) => {
    if (!id) return;

    const signal = nextSignal();

    try {
      setSelectedActivity(null);
      setActivityStatus(UIStatus.Loading);
      setUIState(prev => ({ ...prev, noteId: id, mode, isLoading: true }));
      const endpoint = ENDPOINTS.supportActivityById.replace(':id', id);
      const { data: activityData } = await SECONDARY_API_CLIENT.get(endpoint, { signal });
      if (signal.aborted) return;

      setSelectedActivity(activityData);
      setActivityStatus(UIStatus.Success);
    } catch (error) {
      if (axios.isCancel(error) || signal.aborted) return;

      setActivityStatus(UIStatus.Error);
      dispatch(
        showAlert({
          severity: 'error',
          title: strings.commandCenter.feedbackNotes.noteDetailsDrawer.title,
          body: strings.commandCenter.feedbackNotes.supportActivity.form.error.somethingWrong
        })
      );
    } finally {
      if (!signal.aborted) {
        setUIState(prev => ({
          ...prev,
          isLoading: false
        }));
      }
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
    triggerValidation();

    const formValues = getValues();

    setUIState(prev => ({
      ...prev,
      isSaving: true
    }));

    try {
      const endpoint = ENDPOINTS.supportActivityById.replace(':id', uiState.noteId);
      const notes = { notes: formValues.notes, saveInEmployeeFile: formValues.saveInEmployeeFile };
      const response = await SECONDARY_API_CLIENT.put(endpoint, notes);
      if (response.status === HTTPStatusCodes.Ok || response.status === HTTPStatusCodes.NoContent) {
        dispatch(
          showAlert({
            severity: 'success',
            title: strings.commandCenter.feedbackNotes.noteDetailsDrawer.title,
            body: strings.commandCenter.feedbackNotes.supportActivity.form.success
          })
        );
      }
      getData();
      handleCloseDrawer();
    } catch (error) {
      dispatch(
        showAlert({
          severity: 'error',
          title: strings.commandCenter.feedbackNotes.noteDetailsDrawer.title,
          autoHideDuration: 5000,
          body:
            getErrorMessage(error) ??
            strings.commandCenter.feedbackNotes.supportActivity.form.error.somethingWrong
        })
      );
    } finally {
      setUIState(prev => ({
        ...prev,
        isSaving: false
      }));
    }
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: handleCloseDrawer
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        onProgressText: strings.shared.ui.saving,
        disabled:
          isUploadingEditorTextImage || activityStatus !== UIStatus.Success || uiState.isSaving,
        isSaving: uiState.isSaving
      },
      mode: 'save',
      visible: uiState.mode !== ACTIVITY_MODE.View
    }
  ];

  return (
    <>
      <ScrollableDataTableLayout
        content={
          <ScrollableDataTable
            columnOrder={columnOrder?.length ? columnOrder : undefined}
            columns={finalColumns}
            count={count ?? 0}
            data={data}
            draggableColumns={{
              enabled: true
            }}
            elevation={0}
            filter={false}
            loading={showWholeSkeleton}
            onColumnDisplayClick={handleColumnDisplayChange}
            onColumnOrderChange={handleColumnOrderChange}
            onColumnSortChange={handleColumnSortChange}
            onPageClick={handlePageChange}
            onPerPageClick={handlePerPageChange}
            onSearchTextChange={handleKeywordChange}
            onToggleFilters={handleFiltersToggle}
            page={page}
            refreshing={isLoading}
            rowsPerPage={perPage}
            searchText={keyword}
            selectableRows="none"
            selectToolbarPlacement="none"
            sortOrder={{ name: orderBy, direction }}
            title={strings.commandCenter.feedbackNotes.emptyState.supportActivity.title}
          />
        }
      />
      <When condition={!!uiState.noteId}>
        <FPDrawer
          CardHeaderProps={{
            title:
              ACTIVITY_TYPE_MAP[selectedActivity?.type] ||
              strings.commandCenter.feedbackNotes.noteDetailsDrawer.title
          }}
          ContentWrapper="form"
          ContentWrapperProps={{ onSubmit }}
          FPDrawerProps={{ open: !!uiState.noteId }}
          hasPaddingContent={false}
          onClose={handleCloseDrawer}
          footer={
            <FPButtonBuilder
              GridContainerProps={{ spacing: 2 }}
              buttonDefinitions={buttonDefinitions}
            />
          }
        >
          <StatusManager status={activityStatus}>
            <FormContext {...form}>
              <SupportActivityDetails activity={selectedActivity} mode={uiState.mode} />
            </FormContext>
          </StatusManager>
        </FPDrawer>
      </When>
    </>
  );
};
